import { useStore, selectCreature } from '@/infra/store';
import { useT } from '@/infra/i18n';
import { deriveCareProfileView } from '@/domain/care/care-profile-view';
import { overlayStyle, cardStyle } from '@/presentation/modals/modal-shared';
import { COLORS, FONT, SPACING } from '@/presentation/theme/tokens';

const ASSET_ROOT = '/assets/generated/house';

interface Props { open: boolean; onClose: () => void }

export function CareProfileModal({ open, onClose }: Props) {
  const t = useT();
  const creature = useStore(selectCreature);
  if (!open || !creature) return null;
  const view = deriveCareProfileView(creature);
  const titleId = 'care-profile-title';

  return (
    <div role="dialog" aria-modal="true" aria-labelledby={titleId} style={overlayStyle} onClick={onClose}>
      <div style={{ ...cardStyle, maxWidth: 680 }} onClick={(event) => event.stopPropagation()}>
        <header style={header}>
          <img
            data-testid="care-profile-icon"
            src={`${ASSET_ROOT}/icon-profile.png`}
            alt=""
            draggable={false}
            style={profileIcon}
          />
          <div>
            <h2 id={titleId} style={title}>{t('careProfile.title')}</h2>
            <p style={identity}>{creature.name} · {t(`stage.${creature.lifecycle.stage}`)}</p>
          </div>
        </header>
        <div style={temperamentBox}>
          <strong>{t('careProfile.temperament')}: {view.temperament}</strong>
          <span>{t(view.summaryKey)}</span>
        </div>
        <div style={grid}>
          <Section title={t('careProfile.currentTraits')} tone="#ffd166">
            {view.traitKeys.length ? <div style={chipRow}>{view.traitKeys.map((key) => <span key={key} style={traitChip}>{t(key)}</span>)}</div> : <p style={empty}>{t('careProfile.noTraits')}</p>}
          </Section>
          <Section title={t('careProfile.tendencies')} tone="#8ee6d0">
            <List keys={view.tendencyKeys} translate={t} />
          </Section>
          <div style={{ gridColumn: '1 / -1' }}>
            <Section title={t('careProfile.story')} tone="#ff9ac8">
              <List keys={view.storyKeys} translate={t} />
            </Section>
          </div>
        </div>
        <div style={footer}><button data-testid="care-profile-close" onClick={onClose} style={closeButton}>{t('careProfile.close')}</button></div>
      </div>
    </div>
  );
}

function Section({ title, tone, children }: { title: string; tone: string; children: React.ReactNode }) {
  return <section style={{ ...section, borderTopColor: tone }}><h3 style={{ ...sectionTitle, color: tone }}>{title}</h3>{children}</section>;
}
function List({ keys, translate }: { keys: string[]; translate: (key: string) => string }) {
  return <ul style={list}>{keys.map((key) => <li key={key}>{translate(key)}</li>)}</ul>;
}

const header: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 12 };
const profileIcon: React.CSSProperties = { width: 54, height: 54, objectFit: 'contain', imageRendering: 'auto' };
const title: React.CSSProperties = { margin: 0, color: COLORS.accent, fontFamily: '"Courier New", monospace', textTransform: 'uppercase', fontSize: FONT.sizeXl };
const identity: React.CSSProperties = { margin: '4px 0 0', color: COLORS.textDim, fontSize: FONT.sizeSm };
const temperamentBox: React.CSSProperties = { marginTop: SPACING.md, padding: 12, display: 'grid', gap: 5, background: '#20233b', borderLeft: '4px solid #8ee6d0', color: COLORS.text, fontSize: FONT.sizeSm };
const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 12, marginTop: 14 };
const section: React.CSSProperties = { background: '#101222', borderTop: '3px solid', padding: 13, minHeight: 100 };
const sectionTitle: React.CSSProperties = { margin: '0 0 10px', fontFamily: '"Courier New", monospace', textTransform: 'uppercase', fontSize: 13, letterSpacing: 0.5 };
const chipRow: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 7 };
const traitChip: React.CSSProperties = { padding: '5px 8px', background: '#3b2e28', border: '1px solid #8c6a44', color: '#ffe0a3', fontSize: 12 };
const list: React.CSSProperties = { margin: 0, paddingLeft: 18, color: COLORS.text, fontSize: 13, lineHeight: 1.55 };
const empty: React.CSSProperties = { margin: 0, color: COLORS.textFaint, fontSize: 13 };
const footer: React.CSSProperties = { textAlign: 'right', marginTop: SPACING.md };
const closeButton: React.CSSProperties = { background: COLORS.accent, color: COLORS.bg, border: '2px solid #fff0cf', padding: '8px 16px', fontWeight: 800, cursor: 'pointer', boxShadow: '3px 3px 0 #080812' };
